import { db } from '@/lib/firebase'
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'

// Simplified notification types
interface SimplifiedNotification {
  id: string
  userId: string
  type: 'engagement' | 'request' | 'support' | 'system'
  subtype: string
  message: string
  data: Record<string, any>
  audience: 'web' | 'mobile' | 'all'
  read: boolean
  createdAt: any
}

interface NotificationEvent {
  type: string
  payload: Record<string, any>
  processed: boolean
  createdAt: any
}

/**
 * Unified Notification Service with Company Context
 * Single Responsibility: Handle all notification operations
 * Clean Code: Simple, focused methods with clear names
 * Company-Aware: Supports both personal and company-wide notifications
 */
export class NotificationService {
  private readonly notificationsCollection = 'notifications'
  private readonly eventsCollection = 'events'

  // ✅ COMPANY-AWARE QUERY: Get notifications for user + their company
  async getUserNotifications(
    userId: string,
    platform: 'web' | 'mobile' = 'web',
    companyId?: string,
  ): Promise<SimplifiedNotification[]> {
    if (!userId) return []

    const audiences = platform === 'web' ? ['web', 'all'] : ['mobile', 'all']

    // Query 1: Personal notifications
    const personalQuery = query(
      collection(db, this.notificationsCollection),
      where('userId', '==', userId),
      where('audience', 'in', audiences),
      orderBy('createdAt', 'desc'),
      limit(50),
    )

    const personalSnapshot = await getDocs(personalQuery)
    let allNotifications = personalSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as SimplifiedNotification[]

    // Query 2: Company-wide notifications (if user belongs to a company)
    if (companyId) {
      const companyQuery = query(
        collection(db, this.notificationsCollection),
        where('companyId', '==', companyId),
        where('audience', 'in', audiences),
        orderBy('createdAt', 'desc'),
        limit(25),
      )

      const companySnapshot = await getDocs(companyQuery)
      const companyNotifications = companySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as SimplifiedNotification[]

      // Merge and deduplicate notifications
      const notificationMap = new Map()

      // Add personal notifications first
      for (const notif of allNotifications) {
        notificationMap.set(notif.id, notif)
      }

      // Add company notifications (avoid duplicates)
      for (const notif of companyNotifications) {
        if (!notificationMap.has(notif.id)) {
          notificationMap.set(notif.id, notif)
        }
      }

      // Convert back to array and sort by createdAt
      allNotifications = Array.from(notificationMap.values())
        .sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0
          const bTime = b.createdAt?.seconds || 0
          return bTime - aTime
        })
        .slice(0, 50) // Limit final results
    }

    return allNotifications
  }

  // ✅ COMPANY-AWARE UNREAD COUNT: Count personal + company notifications
  async getUnreadCount(userId: string, platform: 'web' | 'mobile' = 'web', companyId?: string): Promise<number> {
    if (!userId) return 0

    const audiences = platform === 'web' ? ['web', 'all'] : ['mobile', 'all']

    // Query 1: Personal unread notifications
    const personalQuery = query(
      collection(db, this.notificationsCollection),
      where('userId', '==', userId),
      where('audience', 'in', audiences),
      where('read', '==', false),
    )

    const personalSnapshot = await getDocs(personalQuery)
    let totalCount = personalSnapshot.size

    // Query 2: Company-wide unread notifications
    if (companyId) {
      const companyQuery = query(
        collection(db, this.notificationsCollection),
        where('companyId', '==', companyId),
        where('audience', 'in', audiences),
        where('read', '==', false),
      )

      const companySnapshot = await getDocs(companyQuery)

      // Get unique notification IDs to avoid counting duplicates
      const personalIds = new Set(personalSnapshot.docs.map((doc) => doc.id))
      const uniqueCompanyCount = companySnapshot.docs.filter((doc) => !personalIds.has(doc.id)).length

      totalCount += uniqueCompanyCount
    }

    return totalCount
  }

  // ✅ ENHANCED UNREAD COUNT BY TYPE: Company-aware type counting
  async getUnreadCountByType(
    userId: string,
    platform: 'web' | 'mobile' = 'web',
    companyId?: string,
  ): Promise<Record<string, number>> {
    if (!userId) return {}

    // Get all unread notifications using the existing method
    const notifications = await this.getUserNotifications(userId, platform, companyId)
    const unreadNotifications = notifications.filter((n) => !n.read)

    return unreadNotifications.reduce(
      (counts, notification) => {
        counts[notification.type] = (counts[notification.type] || 0) + 1
        return counts
      },
      {} as Record<string, number>,
    )
  }

  async markAsRead(notificationId: string): Promise<void> {
    if (!notificationId || notificationId.trim() === '') {
      throw new Error('Notification ID is required and cannot be empty')
    }

    if (notificationId.startsWith('follow-request-')) {
      throw new Error('Cannot mark transformed request notifications as read')
    }

    await updateDoc(doc(db, this.notificationsCollection, notificationId), {
      read: true,
    })
  }

  // ✅ COMPANY-AWARE MARK ALL AS READ: Mark personal + company notifications as read
  async markAllAsRead(userId: string, platform: 'web' | 'mobile' = 'web', companyId?: string): Promise<void> {
    if (!userId) throw new Error('User ID is required')

    const audiences = platform === 'web' ? ['web', 'all'] : ['mobile', 'all']

    // Query 1: Personal unread notifications
    const personalQuery = query(
      collection(db, this.notificationsCollection),
      where('userId', '==', userId),
      where('audience', 'in', audiences),
      where('read', '==', false),
    )

    const personalSnapshot = await getDocs(personalQuery)

    // Deduplicate promises by document ID to avoid updating the same notification twice
    const uniquePromises = new Map<string, Promise<void>>()

    for (const doc of personalSnapshot.docs) {
      uniquePromises.set(doc.id, updateDoc(doc.ref, { read: true }))
    }

    if (companyId) {
      const companySnapshot = await getDocs(
        query(
          collection(db, this.notificationsCollection),
          where('companyId', '==', companyId),
          where('audience', 'in', audiences),
          where('read', '==', false),
        ),
      )

      for (const doc of companySnapshot.docs) {
        if (!uniquePromises.has(doc.id)) {
          uniquePromises.set(doc.id, updateDoc(doc.ref, { read: true }))
        }
      }
    }

    const finalPromises = Array.from(uniquePromises.values())
    await Promise.all(finalPromises)
  }

  // Event creation methods (enhanced with company context)
  async createNotificationEvent(event: Omit<NotificationEvent, 'processed' | 'createdAt'>): Promise<void> {
    await addDoc(collection(db, this.eventsCollection), {
      ...event,
      processed: false,
      createdAt: serverTimestamp(),
    })
  }

  async createInteractionEvent(payload: {
    type: 'like' | 'comment' | 'share' | 'reply'
    postId: string
    postTitle: string
    userId: string
    userName: string
    userPicUrl?: string
    postAuthorId: string
    companyId?: string
    commentId?: string
    replyToId?: string
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'interaction.created',
      payload,
    })
  }

  async createRequestEvent(payload: {
    requestType: 'follow'
    status: 'accepted' | 'rejected' | 'pending' | 'cancelled'
    userId: string
    companyId: string
    companyName: string
    requestId: string
    userName?: string
    userPicUrl?: string
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'request.status_changed',
      payload,
    })
  }

  async createSupportEvent(payload: {
    ticketId: string
    userId: string
    companyId?: string
    status: string
    subject: string
    message?: string
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'support.status_changed',
      payload,
    })
  }

  async createCompanyAnnouncementEvent(payload: {
    companyId: string
    companyName: string
    title: string
    message: string
    priority: 'low' | 'medium' | 'high'
    audience: 'web' | 'mobile' | 'all'
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'system.company_announcement',
      payload,
    })
  }

  async createUserCountEvent(payload: {
    companyId: string
    action: 'increment' | 'decrement'
    userId: string
    reason: 'user_added' | 'user_removed' | 'user_left'
    metadata?: Record<string, any>
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'user.count_changed',
      payload,
    })
  }

  async createFollowerCountEvent(payload: {
    companyId: string
    action: 'increment' | 'decrement'
    userId: string
    reason: 'follow_accepted' | 'unfollow' | 'user_blocked' | 'user_unblocked'
    metadata?: Record<string, any>
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'follower.count_changed',
      payload,
    })
  }

  /**
   * Create count sync event (to recalculate counts based on actual documents)
   */
  async createCountSyncEvent(payload: {
    companyId: string
    reason?: 'manual_sync' | 'data_integrity_check' | 'migration'
  }): Promise<void> {
    await this.createNotificationEvent({
      type: 'count.sync',
      payload,
    })
  }
}

// Singleton instance
export const notificationService = new NotificationService()
